package com.essntl.uikit.components.input

import androidx.compose.foundation.*
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.LocalTextSelectionColors
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.*
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.label.EsInputLabel
import com.essntl.uikit.components.input.message.EsInputMessage
import com.essntl.uikit.components.input.placeholder.EsInputPlaceholder
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import com.mohamedrejeb.richeditor.model.RichTextState
import com.mohamedrejeb.richeditor.ui.BasicRichTextEditor
import core.validation.EsInputState

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EsRichInput(
    richTextState: RichTextState,
    label: String? = null,
    placeholder: String? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    modifier: Modifier = Modifier,
) {
    val isError =
        remember(state) {
            state == EsInputState.Error
        }
    val isSuccess =
        remember(state) {
            state == EsInputState.Success
        }
    val isLoading =
        remember(state) {
            state == EsInputState.Loading
        }
    val focusRequester = remember { FocusRequester() }

    Column(
        modifier = Modifier,
    ) {
        if (label != null) {
            EsInputLabel(
                label = label,
                modifier = Modifier,
            )

            Spacer(
                modifier = Modifier.height(.5f.spacing),
            )
        }

        val isHovered = interactionSource.collectIsHoveredAsState()
        val isFocused = interactionSource.collectIsFocusedAsState()

        val esColorScheme = MaterialTheme.esColorScheme
        val colors =
            remember(esColorScheme, isHovered.value, cursorColor) {
                getEsInputColors(
                    esColorScheme = esColorScheme,
                    hovered = isHovered.value,
                    cursorColor = cursorColor,
                )
            }
        val selectionColors =
            TextSelectionColors(
                handleColor = MaterialTheme.esColorScheme.textPrimary,
                backgroundColor = MaterialTheme.esColorScheme.primary.copy(.4f),
            )

        val textStyle = MaterialTheme.esTypography.p1Regular16

        // If color is not provided via the text style, use content color as a default
        val textColor =
            textStyle.color.takeOrElse {
                colors.textColor(enabled, isError, interactionSource).value
            }
        val mergedTextStyle = textStyle.merge(TextStyle(color = textColor))

        CompositionLocalProvider(LocalTextSelectionColors provides selectionColors) {
            BasicRichTextEditor(
                state = richTextState,
                readOnly = readOnly,
                textStyle = mergedTextStyle,
                cursorBrush = SolidColor(colors.cursorColor(isError).value),
                keyboardOptions = keyboardOptions,
                keyboardActions = keyboardActions,
                interactionSource = interactionSource,
                singleLine = singleLine,
                maxLines = maxLines,
                minLines = minLines,
                decorationBox = @Composable { innerTextField ->
                    OutlinedTextFieldDefaults.DecorationBox(
                        value = richTextState.annotatedString.text,
                        visualTransformation = VisualTransformation.None,
                        innerTextField = innerTextField,
                        placeholder =
                            with(placeholder) {
                                when {
                                    this != null -> {
                                        { EsInputPlaceholder(this) }
                                    }
                                    else -> null
                                }
                            },
                        prefix =
                            when {
                                prefix != null -> {
                                    {
                                        Box(
                                            modifier =
                                                Modifier
                                                    .padding(end = 1.5f.spacing),
                                        ) {
                                            prefix()
                                        }
                                    }
                                }
                                else -> null
                            },
                        suffix =
                            when {
                                isLoading -> {
                                    {
                                        EsSpinner(
                                            size = EsSpinnerSize.Large,
                                            color = MaterialTheme.esColorScheme.iconPrimary,
                                            modifier = Modifier,
                                        )
                                    }
                                }
                                isError && !isFocused.value -> {
                                    {
                                        EsLargeIcon(
                                            res = EsIcons.InfoCircle,
                                            tint = MaterialTheme.esColorScheme.iconError,
                                        )
                                    }
                                }
                                suffix != null -> {
                                    {
                                        Box(
                                            modifier =
                                                Modifier
                                                    .padding(start = 1.5f.spacing),
                                        ) {
                                            suffix()
                                        }
                                    }
                                }
                                else -> null
                            },
                        singleLine = singleLine,
                        enabled = enabled,
                        isError = isError,
                        interactionSource = interactionSource,
                        colors = colors.toTextFieldColors(),
                        contentPadding =
                            PaddingValues(
                                vertical = 1.spacing,
                                horizontal = 2.spacing,
                            ),
                        container = {
                            ContainerBox(
                                enabled = enabled,
                                isError = isError,
                                isSuccess = isSuccess,
                                interactionSource = interactionSource,
                                colors = colors,
                                shape = shape,
                                focusedBorderThickness = 1.dp,
                                unfocusedBorderThickness = 1.dp,
                            )
                        },
                    )
                },
                modifier =
                    modifier
                        .focusRequester(focusRequester)
                        .hoverable(interactionSource = interactionSource),
            )
        }

        if (message != null) {
            Spacer(
                modifier = Modifier.height(.5f.spacing),
            )

            EsInputMessage(
                message = message,
                state = state.esInputMessageState,
            )
        }
    }
}
